import { Button, buttonClass, cardClass } from '@ledgerloom/ui';
import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApi, usePageTitle } from '../hooks';
import { Breadcrumb, Empty, ErrorBanner, Icon, ListSkeleton, Time, when, ToneBadge } from '../components/ui';
import { UnlockMap, type Lane as WorkLane } from './Approvals';
import { Face, type Tone } from '../components/TeamFace';

// The team's members and the words for what each is doing, shared by the Agent
// Desk (ADR 0039), Home, and a person's page; and the In motion page.

export type Lane = 'attention' | 'working' | 'trial' | 'quiet';
export interface Member {
  kind: 'person' | 'agent'; id: string; name: string; lane: Lane; tone: Tone; situation: string; waiting: number; oldest_waiting_at: string | null;
  role?: string; last_seen_at?: string | null; agents?: number;
  template_id?: string; title?: string | null; class?: string; status?: string; status_reason?: string | null; principal_type?: string; principal_name?: string | null;
  open_exceptions?: number; queued?: number; last_run_at?: string | null; last_run_status?: string | null;
}
export interface Report { id: string; agent_id: string; agent_name: string; template_id: string; status: string; shadow: boolean; summary: string; started_at: string; ended_at: string | null }
export interface TeamNow { generated_at: string; counts: Record<Lane, number>; members: Member[]; reports: Report[] }


const first = (name: string | null | undefined) => (name ?? '').split(/\s+/)[0] || 'a person';
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
export const ROLE_NAME: Record<string, string> = { owner: 'Owner', admin: 'Admin', manager: 'Manager', member: 'Member' };

// The status in a few words, for the chip next to the face.
export function label(m: Member): string {
  if (m.kind === 'person') return m.situation === 'waiting' ? `${m.waiting} waiting` : m.situation === 'active' ? 'Active' : 'Idle';
  switch (m.situation) {
    case 'stuck': return 'Stuck';
    case 'waiting': return `Waiting on ${first(m.principal_name)}`;
    case 'draft': return 'Not signed yet';
    case 'paused': return 'Paused';
    case 'queued': return 'Queued';
    case 'running': return m.lane === 'trial' ? 'On trial, working' : 'Working';
    case 'shadow': return 'On trial';
    default: return 'Idle';
  }
}

// What the member is doing, in one sentence.
export function sentence(m: Member): string {
  if (m.kind === 'person') {
    if (m.situation === 'waiting') return `${plural(m.waiting, 'decision')} waiting.${m.oldest_waiting_at ? ` The oldest arrived ${when(m.oldest_waiting_at)}.` : ''}`;
    if (m.situation === 'active') return 'Active in the last 30 minutes.';
    return m.last_seen_at ? `Last seen ${when(m.last_seen_at)}.` : 'Not signed in yet.';
  }
  switch (m.situation) {
    case 'stuck': return `${plural(m.open_exceptions ?? 0, 'question')} for a person to decide before it can go on.`;
    case 'waiting': return `${plural(m.waiting, 'draft')} waiting for ${first(m.principal_name)}. Nothing is saved until then.`;
    case 'draft': return 'Hired, and waiting for a second person to sign.';
    case 'paused': return m.status_reason || 'Paused. Waiting for someone to tune or retire it.';
    case 'queued': return `${plural(m.queued ?? 0, 'task')} queued.`;
    case 'running': return m.lane === 'trial' ? 'Working now, in shadow: nothing is saved or sent.' : 'Working now.';
    case 'shadow': return 'Its work is checked and logged, never saved or sent.';
    default: return 'Nothing waiting.';
  }
}

const worksFor = (m: Member) => (m.kind === 'person' ? ROLE_NAME[m.role ?? ''] ?? m.role : m.principal_type === 'tenant' ? 'Works for the company' : `Works for ${m.principal_name ?? (m.principal_type === 'team' ? 'a team' : 'a person')}`);
const meta = (m: Member) => m.kind === 'person'
  ? (m.agents ? `${plural(m.agents, 'agent')} ${m.agents === 1 ? 'works' : 'work'} for ${first(m.name)}` : 'No agents yet')
  : m.last_run_at ? `Last run ${when(m.last_run_at)}${m.last_run_status && m.last_run_status !== 'running' ? `, ${m.last_run_status}` : ''}` : 'Has not run yet';

export const memberHref = (m: Member) => (m.kind === 'agent' ? `/agents/${encodeURIComponent(m.id)}` : `/team/people/${encodeURIComponent(m.id)}`);

// The team refreshes on its own so a lane change shows without a reload.
export function useTeam(reports = 6) {
  const team = useApi<TeamNow>(`/v1/team?reports=${reports}`);
  const reload = team.reload;
  useEffect(() => { const t = setInterval(() => void reload(), 15_000); return () => clearInterval(t); }, [reload]);
  return team;
}

export interface Motion { id: string; title: string; topic: string; from: string; band: 'motion' | 'done'; unlocks: WorkLane[]; decided: { by: string | null; at: string; outcome: string } }
const MOTION_BANDS: Array<['motion' | 'done', string]> = [['motion', 'In motion'], ['done', 'Done today']];
export const OUTCOME_WORD: Record<string, string> = { approved: 'Approved', rejected: 'Rejected', edited: 'Sent in their own words', expired: 'Expired' };

// Decided sign-offs and what they set moving (ADR 0038): the Inbox's queue and
// card, read-only, with each step's live state. Refreshes while work runs. A
// page under the Agent Desk (ADR 0039).
export function InMotion() {
  usePageTitle('In motion');
  const m = useApi<{ items: Motion[] }>('/v1/inbox/in-motion');
  const reload = m.reload;
  useEffect(() => { const t = setInterval(() => void reload(), 15_000); return () => clearInterval(t); }, [reload]);
  const [params, setParams] = useSearchParams();
  const items = m.data?.items ?? [];
  const open = items.find((i) => i.id === params.get('item')) ?? null;
  return (
    <div className="stack">
    <Breadcrumb items={[{ to: '/desk', label: 'Agent Desk' }, { label: 'In motion' }]} />
    <div className="page-head">
      <div><h1>In motion</h1><p>Sign-offs once they are decided, and the work each one set going.</p></div>
    </div>
    <div className={`deck${open ? ' has-item' : ''}`}>
      <section className="deck-queue" aria-labelledby="motion-h">
        <h2 id="motion-h" className="sr-only">In motion</h2>
        <ErrorBanner error={m.error} />
        {m.loading && !m.data ? <ListSkeleton rows={4} label="Loading what is in motion" /> : !items.length ? (
          <Empty title="Nothing in motion" action={<Link className={buttonClass({ variant: 'secondary', size: 'sm' })} to="/desk">Open the Agent Desk</Link>}>When a sign-off is decided, the work it starts shows here until it is done.</Empty>
        ) : MOTION_BANDS.map(([band, label]) => {
          const list = items.filter((i) => i.band === band);
          if (!list.length) return null;
          return (
            <div key={band} className="deck-band">
              <h3 className="deck-band-h" id={`mband-${band}`}>{label} <span className="faint">{list.length}</span></h3>
              <ul className="deck-rows" aria-labelledby={`mband-${band}`}>
                {list.map((i) => (
                  <li key={i.id}>
                    <Link className={`deck-row${i.id === open?.id ? ' on' : ''}`} to={`?item=${encodeURIComponent(i.id)}`} aria-current={i.id === open?.id ? 'true' : undefined}>
                      <span className="deck-row-topic">{i.topic}</span>
                      <span className="item-title">{i.title}</span>
                      <span className="item-sub">{OUTCOME_WORD[i.decided.outcome] ?? i.decided.outcome} by {i.decided.by ?? 'someone'} · <Time iso={i.decided.at} /></span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </section>
      <section className="deck-stage" aria-label="Open sign-off">
        {open && <Button type="button" variant="ghost" size="sm" className="deck-back" onClick={() => setParams({})}><Icon name="back" />Back to in motion</Button>}
        {open ? (
          <article className="deck-card" aria-labelledby="motion-item-h">
            <div className="deck-body stack">
              <p className="deck-kicker">{open.topic} · {open.from}</p>
              <h2 id="motion-item-h" tabIndex={-1}>{open.title}</h2>
              <p className="deck-meaning">{OUTCOME_WORD[open.decided.outcome] ?? open.decided.outcome} by {open.decided.by ?? 'someone'} <Time iso={open.decided.at} />.</p>
              <section className="stack" style={{ gap: 8 }} aria-labelledby="motion-unlocks">
                <h3 id="motion-unlocks" className="deck-h">What it set moving</h3>
                <UnlockMap lanes={open.unlocks} gate={{ word: OUTCOME_WORD[open.decided.outcome] ?? open.decided.outcome, tone: open.decided.outcome === 'approved' || open.decided.outcome === 'edited' ? 'approved' : 'rejected', detail: open.band === 'motion' ? 'The work below is still going.' : 'Everything it started has finished.' }} />
              </section>
            </div>
          </article>
        ) : <p className="muted small deck-hint">Choose a sign-off to see what it set moving.</p>}
      </section>
    </div>
    </div>
  );
}

export function MemberCard({ m }: { m: Member }) {
  const body = (
    <>
      <span className="member-top">
        <Face kind={m.kind} name={m.name} template={m.template_id} tone={m.tone} />
        <span className="grow">
          <span className="member-name">{m.name}</span>
          <span className="faint">{m.kind === 'agent' ? m.title ?? 'Agent' : 'Person'} · {worksFor(m)}</span>
        </span>
        <ToneBadge tone={m.tone}>{label(m)}</ToneBadge>
      </span>
      <span className="member-sentence">{sentence(m)}</span>
      <span className="member-meta faint">{meta(m)}</span>
    </>
  );
  return <Link to={memberHref(m)} className={`${cardClass} member`}>{body}</Link>;
}
